import React from "react";
import { NavLink } from "react-router-dom";
import { Building2 } from "lucide-react";
import { NAV_GROUPS } from "../lib/nav";

/**
 * Color-banded health strip.
 *
 *   0-39   → red          (severe stress)
 *   40-59  → amber        (warning band)
 *   60-79  → yellow-green (improving / acceptable)
 *   80-100 → green        (healthy)
 *
 * Stays neutral ("Insufficient data") when no numeric score is available.
 * The band colour applies to the fill, not the text — score readout stays
 * neutral so colour-blind users still read the number.
 */
function HealthBar({ score }) {
  const hasScore = score !== null && score !== undefined && score !== "";
  const safe = hasScore ? Math.max(0, Math.min(100, Number(score) || 0)) : 0;

  // Collapsed to the design system's 3 canonical bands (critical /
  // drifting / on_track) — the system defines no separate "good but
  // not perfect" tier, so 60-100 reads as one on_track band.
  let fillClass = "bg-status-noData";
  if (hasScore) {
    if      (safe < 40)  fillClass = "bg-status-critical";
    else if (safe < 60)  fillClass = "bg-status-drifting";
    else                 fillClass = "bg-status-onTrack";
  }

  return (
    <div data-testid="sidebar-health" className="mt-2">
      <div className="flex items-center justify-between text-[9px] uppercase tracking-wider text-ink-500">
        <span>Health</span>
        <span data-testid="sidebar-health-score" className="text-ink-700 font-semibold">
          {hasScore ? `${safe}/100` : "— / 100"}
        </span>
      </div>
      <div className="mt-1 h-1.5 w-full rounded-full bg-ink-200 overflow-hidden">
        {hasScore ? (
          <div
            data-testid="sidebar-health-fill"
            className={`h-full rounded-full ${fillClass}`}
            style={{ width: `${safe}%`, transition: "width 600ms ease" }}
          />
        ) : null}
      </div>
    </div>
  );
}

/**
 * NavItem — light pill row with a coloured icon chip on the left.
 * Active state: subtle blue tint + brand-blue dot anchor on the right.
 *
 * Persona rail rule: colour is used sparingly. Only the 4 C-suite lenses
 * (CFO/CEO/CMO/CTO) carry their own persona tint; every other module gets
 * a single neutral ink tone so the sidebar reads calm, not like a paint
 * chart (per the Brand Package's tabs/persona-rail guidance).
 */
const ICON_TINT = {
  // OVERVIEW
  executive:             { bg: "bg-blue-50",         fg: "text-blue-800" },
  // C-SUITE — persona colours
  cfo:                   { bg: "bg-persona-cfo-tint", fg: "text-persona-cfo" },
  cto:                   { bg: "bg-persona-cto-tint", fg: "text-persona-cto" },
  ceo:                   { bg: "bg-persona-ceo-tint", fg: "text-persona-ceo" },
  cmo:                   { bg: "bg-persona-cmo-tint", fg: "text-persona-cmo" },
  // INTELLIGENCE — neutral
  briefing:              { bg: "bg-ink-100", fg: "text-ink-600" },
  trends:                { bg: "bg-ink-100", fg: "text-ink-600" },
  financial_sync:        { bg: "bg-ink-100", fg: "text-ink-600" },
  history:               { bg: "bg-ink-100", fg: "text-ink-600" },
  projectile:            { bg: "bg-ink-100", fg: "text-ink-600" },
  forecasting:           { bg: "bg-ink-100", fg: "text-ink-600" },
  alerts:                { bg: "bg-ink-100", fg: "text-ink-600" },
  business_panels:       { bg: "bg-ink-100", fg: "text-ink-600" },
  benchmarking:          { bg: "bg-ink-100", fg: "text-ink-600" },
  supplier_verification: { bg: "bg-ink-100", fg: "text-ink-600" },
  // PARTNERS & TOOLS — neutral
  ca_portal:             { bg: "bg-ink-100", fg: "text-ink-600" },
  advanced_ai:           { bg: "bg-ink-100", fg: "text-ink-600" },
  connect:               { bg: "bg-ink-100", fg: "text-ink-600" },
  integrations:          { bg: "bg-ink-100", fg: "text-ink-600" },
  pricing:               { bg: "bg-ink-100", fg: "text-ink-600" },
};

import {
  IconBoardroom, IconCommandCenter, IconCeo, IconCfo, IconCmo, IconCto,
  IconExecutiveBriefing, IconFinancialTrends, IconFinancialSync,
  IconStatementHistory, IconCompanyTrajectory, IconForecasting,
  IconSmartAlerts, IconBusinessCockpit, IconBenchmarking, IconSupplierAudit,
  IconAdvancedTools, IconCoreConnect, IconCoreFund, IconCoreExpert,
  IconIntegrations, IconBilling,
} from "./icons/CoreIcons";

const ICON_GLYPH = {
  executive:             IconBoardroom,
  command_center:        IconCommandCenter,
  cfo:                   IconCfo,
  cto:                   IconCto,
  ceo:                   IconCeo,
  cmo:                   IconCmo,
  briefing:              IconExecutiveBriefing,
  trends:                IconFinancialTrends,
  financial_sync:        IconFinancialSync,
  history:               IconStatementHistory,
  projectile:            IconCompanyTrajectory,
  forecasting:           IconForecasting,
  alerts:                IconSmartAlerts,
  business_panels:       IconBusinessCockpit,
  benchmarking:          IconBenchmarking,
  supplier_verification: IconSupplierAudit,
  ca_portal:             IconCoreExpert,
  advanced_ai:           IconAdvancedTools,
  connect:               IconCoreConnect,
  fund:                  IconCoreFund,
  expert:                IconCoreExpert,
  integrations:          IconIntegrations,
  pricing:               IconBilling,
};

function NavItem({ item, collapsed, onNavigate }) {
  const tint = ICON_TINT[item.id] || { bg: "bg-ink-100", fg: "text-ink-600" };
  const Glyph = ICON_GLYPH[item.id] || IconBoardroom;
  return (
    <NavLink
      to={item.path}
      end
      title={collapsed ? item.title : undefined}
      onClick={onNavigate}
      data-testid={`nav-${item.id}`}
      className={({ isActive }) =>
        [
          "group flex items-center gap-2.5 mx-2 px-2.5 py-1.5 my-[1px] rounded-lg",
          "text-[12.5px] leading-tight transition-all duration-150",
          collapsed ? "tablet:mx-1.5 tablet:justify-center tablet:px-0" : "",
          isActive
            ? "bg-blue-50 text-ink-950 font-medium"
            : "text-ink-700 hover:bg-ink-50",
        ].join(" ")
      }
    >
      {({ isActive }) => (
        <>
          <span className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${tint.bg}`}>
            <Glyph size={13} className={tint.fg} />
          </span>
          <span className={`flex-1 truncate ${collapsed ? "tablet:hidden" : ""}`}>{item.title}</span>
          {isActive && (
            <span className={`h-1.5 w-1.5 shrink-0 rounded-full bg-blue-800 ${collapsed ? "tablet:hidden" : ""}`} />
          )}
        </>
      )}
    </NavLink>
  );
}

/**
 * Boardroom Sidebar — light theme per attached design.
 *
 * Header: C-ORE logo wordmark (image asset) + "BOARDROOM" overline.
 * Company panel: name + email + color-banded health strip.
 * Nav: grouped (OVERVIEW · C-SUITE · C-ORE INTELLIGENCE · PARTNERS & TOOLS)
 *      with pastel-tinted icon chips per item.
 * Footer: © copy line. Light-mode pill omitted in the codebase for now —
 *   we render the toggle when the theme system ships.
 */
/**
 * Boardroom Sidebar — light theme per attached design.
 *
 * Responsive behaviour (Phase 1, 2026-02 — desktop untouched):
 *   Desktop (≥1200px): fixed, always-visible, full-width [230px] — IDENTICAL
 *     to the pre-responsive layout, `navOpen` has no effect.
 *   Tablet (768–1199px): collapsible icon rail. Default = icon-only [64px],
 *     `navOpen=true` temporarily expands it to a full [230px] overlay
 *     (elevated shadow, doesn't reflow content) via the TopBar hamburger.
 *   Mobile (≤767px): fully off-canvas by default (`-translate-x-full`),
 *     slides in as a full [230px] drawer with a backdrop when `navOpen=true`.
 *
 * Header: C-ORE logo wordmark (image asset) + "BOARDROOM" overline.
 * Company panel: name + email + color-banded health strip.
 * Nav: grouped (OVERVIEW · C-SUITE · C-ORE INTELLIGENCE · PARTNERS & TOOLS)
 *      with pastel-tinted icon chips per item.
 * Footer: © copy line. Light-mode pill omitted in the codebase for now —
 *   we render the toggle when the theme system ships.
 */
export default function Sidebar({ company, offsetTop = 0, navOpen = false, onClose }) {
  // Icon-only rail applies ONLY inside the bounded tablet range (768–1199px)
  // and ONLY when not expanded — the `tablet:` utility classes it drives can
  // never match at desktop widths, so desktop stays pixel-identical.
  const collapsed = !navOpen;

  return (
    <>
      {/* Backdrop — mobile drawer + tablet expanded-overlay only. Never
          rendered/visible at desktop (hamburger doesn't exist there, so
          navOpen stays false, but the class guard is kept for safety). */}
      {navOpen && (
        <div
          data-testid="sidebar-backdrop"
          onClick={onClose}
          className="fixed inset-0 z-40 bg-ink-950/40 desktop:hidden"
        />
      )}

      <aside
        data-testid="sidebar"
        className={[
          "fixed left-0 top-0 z-50 flex flex-col bg-white text-ink-950 border-r border-ink-200",
          "transition-all duration-300 ease-out",
          // Mobile (≤767px): off-canvas drawer, full label width when open.
          "w-[230px]",
          navOpen ? "translate-x-0 shadow-modal" : "-translate-x-full",
          // Tablet (768–1199px): always visible; icon rail unless expanded.
          "tablet:translate-x-0",
          navOpen ? "tablet:w-[230px] tablet:shadow-hover" : "tablet:w-16",
          // Desktop (≥1200px): unchanged — always full, static, no overlay.
          "desktop:w-[230px] desktop:translate-x-0 desktop:shadow-none",
        ].join(" ")}
        style={{ top: offsetTop, height: `calc(100vh - ${offsetTop}px)` }}
      >
        {/* Logo */}
        <div className={`px-4 pt-5 pb-3 ${collapsed ? "tablet:px-0 tablet:flex tablet:justify-center" : ""}`}>
          <div data-testid="sidebar-logo" className="leading-tight">
            {collapsed ? (
              <span className="hidden tablet:inline-flex h-8 w-8 items-center justify-center rounded-md bg-blue-50">
                <Building2 size={16} className="text-blue-800" />
              </span>
            ) : null}
            <div className={collapsed ? "tablet:hidden" : ""}>
              <img
                src="/c-ore-logo.png"
                alt="C-ORE"
                style={{ height: 28, width: 100, objectFit: "contain", display: "block", marginLeft: -3.65 }}
                draggable="false"
              />
              <div className="mt-1 text-[9.5px] font-semibold uppercase tracking-[0.28em] text-ink-500">
                Boardroom
              </div>
            </div>
          </div>
        </div>

        {/* Company panel */}
        <div className={`mx-3 rounded-lg bg-white border border-ink-200 px-3 py-2.5 shadow-card ${collapsed ? "tablet:hidden" : ""}`}>
          <div className="flex items-start gap-2.5">
            <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-blue-50">
              <Building2 size={15} className="text-blue-800" />
            </span>
            <div className="min-w-0 flex-1">
              <div data-testid="sidebar-company-name"
                   className="text-[12.5px] font-semibold text-ink-950 leading-tight line-clamp-2">
                {company?.name || "Loading…"}
              </div>
              <div data-testid="sidebar-company-subtitle"
                   className="text-[10.5px] text-ink-500 truncate">
                {company?.subtitle || ""}
              </div>
            </div>
          </div>
          <HealthBar score={company?.health_score} />
        </div>

        {/* Navigation */}
        <nav className="mt-4 flex-1 overflow-y-auto overflow-x-hidden pb-4">
          {NAV_GROUPS.map((group) => (
            <div key={group.id} className="mt-3 first:mt-0">
              <div className={`px-4 pb-1.5 text-[9.5px] font-semibold uppercase tracking-[0.18em] text-ink-400 ${collapsed ? "tablet:hidden" : ""}`}>
                {group.label}
              </div>
              <div className="flex flex-col">
                {group.items.map((item) => (
                  <NavItem key={item.id} item={item} collapsed={collapsed} onNavigate={onClose} />
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className={`px-4 py-3 border-t border-ink-100 ${collapsed ? "tablet:hidden" : ""}`}>
          <div className="text-[10px] text-ink-400">© 2026 C-ORE · Boardroom</div>
        </div>
      </aside>
    </>
  );
}
